import { useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { textFormatOptions, TextFormatOption } from "../utils/textFormatting";

interface UseTextFormattingProps {
  editor: Editor | null;
}

export const useTextFormatting = ({ editor }: UseTextFormattingProps) => {
  const [showTextFormatDropdown, setShowTextFormatDropdown] = useState(false);
  const [currentTextFormat, setCurrentTextFormat] = useState(
    textFormatOptions[0]
  );

  // Function to get current text format based on editor state
  const getCurrentTextFormat = (): TextFormatOption => {
    if (!editor) return textFormatOptions[0];

    if (editor.isActive("heading", { level: 1 })) {
      return textFormatOptions[1];
    } else if (editor.isActive("heading", { level: 2 })) {
      return textFormatOptions[2];
    } else if (editor.isActive("heading", { level: 3 })) {
      return textFormatOptions[3];
    } else {
      return textFormatOptions[0];
    }
  };

  // Update current format when editor selection changes
  useEffect(() => {
    if (editor) {
      const updateCurrentFormat = () => {
        setCurrentTextFormat(getCurrentTextFormat());
      };

      editor.on("selectionUpdate", updateCurrentFormat);
      editor.on("transaction", updateCurrentFormat);

      return () => {
        editor.off("selectionUpdate", updateCurrentFormat);
        editor.off("transaction", updateCurrentFormat);
      };
    }
  }, [editor]);

  const handleTextFormatChange = (option: TextFormatOption) => {
    setCurrentTextFormat(option);
    setShowTextFormatDropdown(false);

    if (!editor) return;

    if (option.level === null) {
      editor.chain().focus().setParagraph().run();
    } else {
      // Convert to heading with specified level
      editor
        .chain()
        .focus()
        .setHeading({ level: option.level as 1 | 2 | 3 })
        .run();
    }
  };

  const toggleBold = () => editor?.chain().focus().toggleBold().run();
  const toggleItalic = () => editor?.chain().focus().toggleItalic().run();
  const toggleBulletList = () =>
    editor?.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () =>
    editor?.chain().focus().toggleOrderedList().run();

  return {
    showTextFormatDropdown,
    setShowTextFormatDropdown,
    currentTextFormat,
    handleTextFormatChange,
    toggleBold,
    toggleItalic,
    toggleBulletList,
    toggleOrderedList
  };
};
